/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Grid } from '@/components/Grid';
import { clickTrack } from '@/utils/analytics/clickTrack';
import styles from './styles.module.scss';
import type { ContentStackParttypeGrid } from '../../interface';
import { type Props as HeadlineProps } from '@/components/Headline/Headline';
import { CMSGridItem } from './CMSGridItem/CMSGridItem';
import cx from 'classnames';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import { Icon, View, Text } from '@az/starc-ui';
import { ArrowRight } from '@az/starc-ui-icons';
import { SmartLink as Link } from '@/utils/smartLink';
import { CMSGridMobileItem } from './CMSGridMobile/CMSGridMobile';

type Props = {
  content: ContentStackParttypeGrid;
  headlineOptions?: HeadlineProps;
  onAnalyticsClick?: () => void;
};

const trackHomeLinkInteractionBySection = (linkName: string, section: string, id: string) => {
  clickTrack({
    homeLinkName: linkName,
    linkBySection: section,
    clickedProductId: id,
    eventType: 'homeInteractionsBySection',
  });
};

/**
 * CMSGrid component - Corresponds to "Part Type Grid" in Contentstack
 *
 * Renders a grid layout of items with optional headline, images, labels, and links.
 * Supports multiple layouts (Four, Five, Six), grid lines, and mobile horizontal scroll.
 *
 * @param content - The Part Type Grid content from Contentstack
 * @param headlineOptions - Optional headline styling options
 * @param onAnalyticsClick - Optional analytics callback for grid interactions
 *
 * Features:
 * - Renders CMSGridItem (standard) or CMSGridMobileItem (horizontal scroll) based on mobile_horizontal_scroll flag
 * - Supports grid_lines for bordered layout
 * - Tracks user interactions via analytics
 */
export const CMSGrid = ({ content, headlineOptions, onAnalyticsClick }: Props) => {
  const { headline, grid_content: gridContent, layout, grid_lines: gridLines } = content;
  const underline = content.underline;
  const link = content.link;
  const isHorizontalScrollEnabled = !content.mobile_horizontal_scroll_disabled;
  const handleLinkClick = (linkName: string | undefined, link: string) => {
    const sectionName =
      (headline ?? '')
        .replace(/<[^>]+>/gi, '')
        .replace(/&nbsp;/gi, '')
        .trim() || '';
    trackHomeLinkInteractionBySection(linkName ?? link, sectionName, '');
  };

  return (
    <Grid container spacing={0} data-testid="cms-grid">
      <CMSHeadline {...headlineOptions} underline={underline ?? false}>
        {headline}
      </CMSHeadline>
      <Grid
        container
        spacing={0}
        className={cx(styles.gridItemsContainer, {
          [styles.gridItemsContainerLines]: gridLines,
          [styles.mobileContainer]: isHorizontalScrollEnabled,
        })}
        onClick={onAnalyticsClick}
      >
        <ul>
          {gridContent?.map((item, i) =>
            isHorizontalScrollEnabled ? (
              <CMSGridMobileItem
                key={i}
                item={item}
                onLinkClick={handleLinkClick}
                layout={layout}
                gridLines={gridLines}
              />
            ) : (
              <CMSGridItem
                key={i}
                item={item}
                onLinkClick={handleLinkClick}
                layout={layout}
                gridLines={gridLines}
              />
            ),
          )}
        </ul>
      </Grid>
      {link?.href && (
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingTop: '22px' }}
        >
          <View>
            <Text weight="medium" variant="main-body" size="100" textCase="uppercase">
              <Link to={link.href} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {link.title}
                <Icon svg={ArrowRight} size={6} color="accent" />
              </Link>
            </Text>
          </View>
        </div>
      )}
    </Grid>
  );
};
